import { Component } from '@theme/component';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { getIOSVersion, mediaQueryLarge } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const resourceCard = /** @type {HTMLElement | null} */ (this.closest('.resource-card'));
    const resourceLink = /** @type {HTMLAnchorElement | null} */ (resourceCard?.querySelector('.resource-card__link'));
    const jsProductCardLink = {
      href: this.closest('.js-product-card')?.dataset.productUrl,
    };
    const productLink =
      productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink() || resourceLink || jsProductCardLink;

    if (!productLink?.href) return '';

    const url = new URL(productLink.href, location.origin);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          // Strip blocks that must not render inside the quick-add modal (e.g. Pairs Well With,
          // whose own quick-add components would otherwise nest inside this modal).
          productGrid.querySelectorAll('[data-quick-add-exclude]').forEach((el) => el.remove());
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    const slideshowSlides = dialogComponent.querySelector('.product-information__media slideshow-slides');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    slideshowSlides?.scrollTo({ left: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    morph(modalContent, productGrid);

    this.#injectViewFullDetailsLink(modalContent);
    this.#syncVariantSelection(modalContent);
  }

  /**
   * Injects a "View full details" link after the buy-buttons-block.
   * The element is re-injected on every morph since morph replaces DOM from the product page,
   * which does not include this quick-add-only element.
   * @param {Element} modalContent
   */
  #injectViewFullDetailsLink(modalContent) {
    const buyButtonsBlock = modalContent.querySelector('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    modalContent.querySelector('.view-more-details__wrapper')?.remove();

    const wrapper = document.createElement('div');
    wrapper.className = 'view-more-details__wrapper';

    const link = document.createElement('a');
    link.className = 'view-more-details button--link';
    link.href = this.productPageUrl;
    link.textContent = 'View full details';

    wrapper.appendChild(link);
    buyButtonsBlock.appendChild(wrapper);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const html = /** @type {Document | undefined} */ (event.detail?.data?.html);
    const productPageHref = QuickAddDialog.#resolveProductPageHref(html, event.detail?.data);

    const viewMoreDetailsLink = /** @type {HTMLAnchorElement | null} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement | null} */ (this.querySelector('.product-header a'));
    let viewFullDetailsLink = /** @type {HTMLAnchorElement | null} */ (
      this.querySelector('.view-more-details__wrapper a')
    );

    if (productPageHref) {
      if (viewMoreDetailsLink) viewMoreDetailsLink.href = productPageHref;
      if (mobileProductTitle) mobileProductTitle.href = productPageHref;
    }

    if (!viewFullDetailsLink && productPageHref) {
      viewFullDetailsLink = this.#ensureViewFullDetailsLink(productPageHref);
    } else if (viewFullDetailsLink && productPageHref) {
      viewFullDetailsLink.href = productPageHref;
    }
  };

  /**
   * PDP URL for the current variant response. Scoped selectors avoid a wrong first match; fallbacks
   * cover themes where `.view-product-title` is absent in the fragment or combined listings.
   * @param {Document | undefined} html
   * @param {{ newProduct?: { id?: string; url?: string } }} [data]
   * @returns {string | null}
   */
  static #resolveProductPageHref(html, data) {
    if (!html) return null;

    const tryHref = (/** @type {Element | null | undefined} */ el) =>
      el instanceof HTMLAnchorElement && el.href ? el.href : null;

    const scoped =
      tryHref(html.querySelector('[data-product-grid-content] .view-product-title a')) ||
      tryHref(html.querySelector('.product-details .view-product-title a')) ||
      tryHref(html.querySelector('.view-product-title a'));
    if (scoped) return scoped;

    const gridPicker = html.querySelector('[data-product-grid-content] variant-picker[data-product-url]');
    const anyPicker = gridPicker ?? html.querySelector('variant-picker[data-product-url]');
    const path = anyPicker instanceof HTMLElement ? anyPicker.dataset.productUrl : undefined;
    if (path) {
      try {
        return new URL(path, window.location.origin).href;
      } catch {
        return null;
      }
    }

    const canonical = html.querySelector('link[rel="canonical"]')?.getAttribute('href');
    if (canonical) {
      try {
        return new URL(canonical, window.location.origin).href;
      } catch {
        return null;
      }
    }

    const newProductUrl = data?.newProduct?.url;
    if (newProductUrl) {
      try {
        return new URL(newProductUrl, window.location.origin).href;
      } catch {
        return null;
      }
    }

    return null;
  }

  /**
   * Re-appends the quick-view-only "View full details" link after combined-listing morphs replace the grid.
   * @param {string} href
   * @returns {HTMLAnchorElement | null}
   */
  #ensureViewFullDetailsLink(href) {
    const modalContent = this.querySelector('#quick-add-modal-content');
    const buyButtonsBlock = modalContent?.querySelector('.buy-buttons-block');
    if (!modalContent || !buyButtonsBlock) return null;

    modalContent.querySelector('.view-more-details__wrapper')?.remove();

    const wrapper = document.createElement('div');
    wrapper.className = 'view-more-details__wrapper';

    const link = document.createElement('a');
    link.className = 'view-more-details button--link';
    link.href = href;
    link.textContent = 'View full details';

    wrapper.appendChild(link);
    buyButtonsBlock.appendChild(wrapper);
    return link;
  }

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
